<template>
  <ValidationObserver ref="form" @submit.prevent="handleSubmit">
    <form>
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">
            {{ tournamentId ? "Edit Tournament" : "Create Tournament" }}
          </p>

          <button type="button" class="delete" @click="$emit('close')" />
        </header>

        <section class="modal-card-body has-text-left">
          <div class="is-flex is-justify-content-space-between">
            <ValidationProvider
              rules="required|regex:^[a-zA-Z0-9].*"
              v-slot="{ errors }"
              name="Tournament name"
              style="width: 100%"
            >
              <b-field
                label="Name"
                label-position="inside"
                :type="errors.length ? 'is-danger' : ''"
                :message="
                  errors.length
                    ? 'Tournament name must start with an alphanumeric character'
                    : ''
                "
              >
                <b-input
                  v-model="formProps.name"
                  placeholder="Tournament name"
                ></b-input>
              </b-field>
            </ValidationProvider>
          </div>

          <div class="is-flex is-justify-content-space-between mt-5">
            <ValidationProvider
              rules="required"
              v-slot="{ errors }"
              name="Start Time"
            >
              <b-field
                label="Start Time"
                label-position="inside"
                :type="errors.length ? 'is-danger' : ''"
                :message="errors.length ? errors[0] : ''"
              >
                <b-datetimepicker
                  v-model="formProps.startTime"
                  placeholder="Type or select a date..."
                  editable
                  :disabled="hasStarted"
                  icon="calendar"
                  :timepicker="{ incrementMinutes: 5 }"
                >
                  <template #left>
                    <b-button
                      label="Now"
                      type="is-primary"
                      icon-left="clock"
                      @click="formProps.startTime = new Date()"
                    />
                  </template>

                  <template #right>
                    <b-button
                      label="Clear"
                      type="is-danger"
                      icon-left="eraser"
                      outlined
                      @click="formProps.startTime = null"
                    />
                  </template>
                </b-datetimepicker>
              </b-field>
            </ValidationProvider>

            <b-field
              label="Finish Time"
              label-position="inside"
              v-if="formProps.startTime"
            >
              <b-timepicker
                v-model="formProps.finishTime"
                placeholder="Finish Time (optional)"
                editable
                :disabled="hasFinished"
                icon="clock"
                :incrementMinutes="15"
                :min-time="formProps.startTime"
                :time-creator="
                  () => new Date(formProps.finishTime || formProps.startTime)
                "
              ></b-timepicker>
            </b-field>
          </div>

          <div class="is-flex is-justify-content-space-between mt-5">
            <b-field label="Total Rounds" style="width: 150px">
              <b-numberinput
                controls-alignment="right"
                controls-position="compact"
                :min="tournament ? tournament.currentRound : 1"
                v-model="formProps.totalRounds"
                :disabled="hasStarted"
              ></b-numberinput>
            </b-field>

            <b-field label="BaseTimeControl" style="width: 150px">
              <b-numberinput
                :disabled="hasStarted"
                :controls="!hasStarted"
                controls-alignment="right"
                controls-position="compact"
                min="1"
                v-model="formProps.baseTimeControl"
              ></b-numberinput>
            </b-field>

            <b-field label="Increment" style="width: 150px">
              <b-numberinput
                :disabled="hasStarted"
                :controls="false"
                controls-alignment="right"
                controls-position="compact"
                min="0"
                v-model="formProps.increment"
              ></b-numberinput>
            </b-field>
          </div>

          <div class="is-flex is-justify-content-space-between mt-5">
            <b-field label="Player Limit" style="width: 150px">
              <b-numberinput
                :disabled="hasFinished"
                controls-alignment="right"
                controls-position="compact"
                min="0"
                :step="5"
                :min-step="1"
                v-model="formProps.playerLimit"
              ></b-numberinput>
            </b-field>

            <b-field label="Min Rating" style="width: 150px">
              <b-numberinput
                :disabled="hasFinished"
                :controls="false"
                controls-alignment="right"
                controls-position="compact"
                min="0"
                v-model="formProps.minRating"
              ></b-numberinput>
            </b-field>

            <b-field label="Max Rating" style="width: 150px">
              <b-numberinput
                :disabled="hasFinished"
                :controls="false"
                controls-alignment="right"
                controls-position="compact"
                min="0"
                v-model="formProps.maxRating"
              ></b-numberinput>
            </b-field>
          </div>

          <div class="is-flex is-justify-content-space-between mt-5">
            <b-field label="Type" label-position="inside">
              <b-select v-model="formProps.type" :disabled="hasStarted">
                <option value="Swiss" key="typeSwiss">Swiss</option>

                <option value="Robin" key="typeRobin">RR</option>

                <option value="Elim" key="typeElim">Knockout</option>
              </b-select>
            </b-field>

            <b-field
              label="Double RR"
              label-position="inside"
              v-show="formProps.type === 'Robin'"
            >
              <b-select
                v-model="formProps.doubleRR"
                :disabled="true || hasStarted"
              >
                <option :value="false" key="doubleFalse">true</option>

                <option :value="true" key="doubleTrue">false</option>
              </b-select>
            </b-field>

            <b-field
              label="Group Size"
              label-position="inside"
              v-show="formProps.type === 'Robin'"
            >
              <b-numberinput
                disabled
                :controls="false"
                controls-alignment="right"
                controls-position="compact"
                min="0"
                v-model="formProps.groupSize"
              ></b-numberinput>
            </b-field>
          </div>

          <div class="is-flex is-justify-content-space-between mt-5">
            <b-field label="Privacy" label-position="inside">
              <b-select v-model="formProps.privacy" :disabled="hasFinished">
                <option
                  value="Private"
                  key="privacyPrivate"
                  v-if="permission.admin || permission.createTournament"
                >
                  Private
                </option>

                <option
                  value="Open"
                  key="privacyOpen"
                  v-if="permission.admin || permission.createTournament"
                >
                  Open
                </option>

                <option
                  value="Invite"
                  key="privacyInvite"
                  v-if="permission.admin"
                >
                  Invite
                </option>

                <option value="Paid" key="privacyPaid" v-if="permission.admin">
                  Paid
                </option>

                <option
                  value="Password"
                  key="privacyPassword"
                  v-if="permission.admin"
                >
                  Password
                </option>
              </b-select>
            </b-field>

            <b-field
              label="Password"
              label-position="inside"
              v-show="formProps.privacy === 'Password'"
            >
              <b-input
                :disabled="hasFinished"
                v-show="formProps.privacy === 'Password'"
                v-model="formProps.password"
                placeholder="secret code"
              ></b-input>
            </b-field>

            <b-field
              label="Product"
              label-position="inside"
              v-if="formProps.privacy === 'Paid'"
            >
              <b-input
                :disabled="hasFinished"
                v-model="formProps.productId"
                ellipsis
                placeholder="Add ProductId"
                style="width: 200px"
              ></b-input>
            </b-field>

            <b-field
              label="Plans"
              label-position="inside"
              v-if="formProps.privacy === 'Paid'"
            >
              <b-taginput
                :disabled="hasFinished"
                v-model="formProps.planIds"
                ellipsis
                placeholder="Add PlanId"
                aria-close-label="Delete this tag"
                style="width: 240px"
              ></b-taginput>
            </b-field>
          </div>

          <div class="is-flex is-justify-content-space-between mt-5">
            <b-field label="Round Mode" label-position="inside">
              <b-select v-model="formProps.roundMode" :disabled="hasFinished">
                <option value="Manual" key="roundModeManual">Fixed time</option>

                <option value="Auto" key="roundModeAuto">Auto start</option>
              </b-select>
            </b-field>

            <b-field label="Reward" label-position="inside">
              <b-select v-model="formProps.reward">
                <option :value="null" key="rewardNone">None</option>

                <option :value="REWARD.TROPHY" key="rewardTrophy">
                  Trophy
                </option>

                <option :value="REWARD.MEDAL" key="rewardMedal">Medal</option>
              </b-select>
            </b-field>

            <b-field label="Reward Participation">
              <b-checkbox v-model="formProps.rewardParticipation"></b-checkbox>
            </b-field>
          </div>

          <div class="is-flex is-justify-content-space-between mt-5">
            <b-tooltip
              style="width: 100%"
              type="is-light"
              :triggers="['click']"
              :auto-close="['outside', 'escape']"
              @open="handleStartingPositionViewerOpen"
            >
              <template v-slot:content>
                <ViewerChessboard
                  v-if="showStartingPositionViewerBoard && formProps.initialFen"
                  :fen="formProps.initialFen"
                />
              </template>

              <b-field label="Starting position" label-position="inside">
                <b-input
                  v-model="formProps.initialFen"
                  placeholder="initial FEN"
                ></b-input>
              </b-field>
            </b-tooltip>
          </div>

          <div
            class="is-flex is-flex-direction-column is-justify-content-start is-align-items-start mt-5"
          >
            <div
              class="is-flex is-justify-content-start is-align-items-center mb-2"
            >
              <b-button
                :label="`Auto repeat`"
                type="is-info"
                :disabled="!formProps.startTime || recurrence.nextOccurrenceId"
                @click="openRecurrenceForm"
              />

              <b-icon
                :icon="recurrence.repeat ? 'check' : 'times'"
                :type="recurrence.repeat ? 'is-success' : 'is-danger'"
                size="is-small"
                class="pl-2"
              ></b-icon>

              <span class="is-size-6 p-2" v-if="recurrence.repeat">
                {{ recurrence.text }}
              </span>

              <span class="is-size-6 p-2" v-else> Not set </span>
            </div>

            <p class="is-size-6" v-if="tournament.occurrence">
              Current occurrence: {{ tournament.occurrence }}
            </p>

            <p class="is-size-6" v-if="nextOccurrenceAt">
              Next coming occurrence at: {{ nextOccurrenceAt | asDate }}
              {{ nextOccurrenceAt | asDayjsFormat("hh:mm A (UTCZ)") }}
            </p>

            <div
              v-if="
                recurrence &&
                recurrence.ends &&
                recurrence.ends.type === 'never'
              "
            >
              <p class="is-size-6">
                Last occurrence:
                <b-icon
                  icon="infinity"
                  size="is-small"
                  style="vertical-align: middle"
                ></b-icon>
              </p>

              <p class="is-size-6">
                Remaining occurrences:
                <b-icon
                  icon="infinity"
                  size="is-small"
                  style="vertical-align: middle"
                ></b-icon>
              </p>
            </div>

            <div v-else>
              <p class="is-size-6">
                Last occurrence at: {{ lastOccurrenceAt | asDate }}
                {{ lastOccurrenceAt | asDayjsFormat("hh:mm A (UTCZ)") }}
              </p>

              <p class="is-size-6" v-if="remainOccurrences">
                Remaining occurrences: {{ remainOccurrences }}
              </p>
            </div>

            <p
              class="is-size-6"
              v-if="!remainOccurrences || remainOccurrences < 0"
            >
              No coming occurrences
            </p>

            <p class="is-size-6" v-if="recurrence.previousOccurrenceId">
              Prev occurrence:
              <router-link
                :to="`/admin/tournaments/${recurrence.previousOccurrenceId}`"
                class="has-text-info"
              >
                {{ recurrence.previousOccurrenceId }}
              </router-link>
            </p>

            <p class="is-size-6" v-if="recurrence.nextOccurrenceId">
              Next occurrence:
              <router-link
                :to="`/admin/tournaments/${recurrence.nextOccurrenceId}`"
                class="has-text-info"
              >
                {{ recurrence.nextOccurrenceId }}
              </router-link>
            </p>
          </div>
        </section>

        <footer class="modal-card-foot is-justify-content-flex-end">
          <b-button label="Cancel" @click="$emit('close')" />

          <b-button label="Submit" type="is-primary" native-type="submit" />
        </footer>

        <b-modal
          v-model="showRecurrenceForm"
          has-modal-card
          trap-focus
          :destroy-on-hide="true"
        >
          <template #default="props">
            <RecurrenceForm
              :initialSettings="recurrence"
              @submit="setRecurrence"
              @close="props.close"
            />
          </template>
        </b-modal>
      </div>
    </form>
  </ValidationObserver>
</template>

<script>
import ViewerChessboard from "@/components/variants/ViewerChessboard.vue";
import { firestore } from "@/services/firebase";
import { REWARD } from "../../constants";
import { mapGetters } from "vuex";
import RecurrenceForm from "@/components/admins/RecurrenceForm.vue";
import { getNextOccurrences } from "vue-chess-shared/tournament/helper";

export default {
  components: { ViewerChessboard, RecurrenceForm },

  props: ["tournament"],

  data() {
    return {
      tournamentId: null,
      formProps: {},
      REWARD: REWARD,
      showStartingPositionViewerBoard: false,
      showRecurrenceForm: false,
      recurrence: this.tournament.recurrence || {},
      nextOccurrenceAt: null,
      lastOccurrenceAt: null,
      remainOccurrences: null,
    };
  },

  computed: {
    ...mapGetters({
      permission: "getPermission",
      currentPlayer: "getPlayer",
    }),

    hasStarted() {
      return this.tournament && !!this.tournament.currentRound;
    },

    hasFinished() {
      if (
        this.tournament &&
        this.tournament.currentRound >= this.tournament.totalRounds
      ) {
        return true;
      }
      return false;
    },
  },

  methods: {
    async handleSubmit() {
      const valid = await this.$refs.form.validate();
      if (!valid) {
        return;
      }

      this.tournamentId ? this.updateTournament() : this.createTournament();
    },

    createTournament() {
      firestore
        .collection("tournaments")
        .add({
          ...this.formProps,
          currentRound: 0, // Start with no rounds active
          createdTime: new Date(),
          recurrence: {
            ...this.recurrence,
            createdTime: new Date(),
          },
          occurrence: 1, // always 1 when manually created. Auto increment +1 when auto-repeated
          ownerId: this.currentPlayer.user_id,
        })
        .then((docRef) => {
          this.$emit("close");
          // Redirect to the newly created tournament's detail/pipeline page
          this.$router.push(`/admin/tournaments/${docRef.id}`);
        });
    },

    updateTournament() {
      firestore
        .collection("tournaments")
        .doc(this.tournamentId)
        .update({
          ...this.formProps,
          updatedTime: new Date(),
          recurrence: this.recurrence,
        })
        .then(this.$emit("close"));
    },

    handleStartingPositionViewerOpen() {
      this.showStartingPositionViewerBoard = true;
    },

    openRecurrenceForm() {
      this.showRecurrenceForm = true;
    },

    setRecurrence(recurrence) {
      this.showRecurrenceForm = false;
      this.nextOccurrenceAt = null;
      this.lastOccurrenceAt = null;
      this.remainOccurrences = null;

      this.recurrence = recurrence || {};

      // preserve previous & next when changing settings
      if (this.tournament.recurrence?.previousOccurrenceId) {
        this.recurrence.previousOccurrenceId =
          this.tournament.recurrence?.previousOccurrenceId;
      }
      if (this.tournament.recurrence?.nextOccurrenceId) {
        this.recurrence.nextOccurrenceId =
          this.tournament.recurrence?.nextOccurrenceId;
      }

      if (!this.recurrence.createdTime) {
        this.recurrence.createdTime = new Date();
      }

      const currentOccurrence = this.tournament.occurrence ?? 1;
      const futureDates = getNextOccurrences(
        this.tournament.startTime,
        currentOccurrence,
        this.recurrence
      );

      this.nextOccurrenceAt = futureDates.at(0);
      this.lastOccurrenceAt = futureDates.at(-1);
      this.remainOccurrences = futureDates.length;
    },
  },

  created() {
    this.tournamentId = this.tournament ? this.tournament.id : null;
    this.formProps = this.tournament;
    if (this.formProps.startTime) {
      this.formProps.startTime = this.formProps.startTime.toDate();
    }
    if (this.formProps.finishTime) {
      this.formProps.finishTime = this.formProps.finishTime.toDate();
    }
    if (this.tournament) {
      this.setRecurrence(this.tournament.recurrence);
    }
  },
};
</script>

<style></style>
